import {inject} from '@angular/core';
import {
  catchError,
  combineLatest,
  concatMap,
  exhaustMap, filter,
  finalize,
  map,
  mergeMap,
  of, switchMap,
  take, tap,
  withLatestFrom
} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from "@ngrx/store";
import {CommonsFacadeService} from "../../../commons/angular/state/commons/commons-facade.service";
import {RoutesHttpService} from "../services/routes-http.service";
import {RouteModel} from "../../../routes/models/route.model";
import {RoutesActions, RoutesAPiActions, RoutesChannelActions} from "./routes.action";
import {routesFeature} from "./routes.state";
import {RoutesChannelService} from "../services/routes-channel.service";


export const getInfiniteScrollRoutes = createEffect(
  (actions$ = inject(Actions),
   routesHttpService = inject(RoutesHttpService),
   routesStore = inject(Store<RouteModel>),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(RoutesAPiActions.getInfiniteScrollRoutes),
      concatMap(payload =>
        of(payload).pipe(
          withLatestFrom(routesStore.select(routesFeature.selectCursor()))
        )
      ),
      mergeMap(([payload, cursor]) => {
        commonsFacadeService.setLoaderOn();
        let paramsCopy = {...payload.params}
        if (!!cursor) {
          paramsCopy.cursor = cursor;
        }
        return routesHttpService.getRoutes(paramsCopy).pipe(
          map((res: {
            list: RouteModel[]
            cursor: string
          }) => RoutesAPiActions.getInfiniteScrollRoutesSuccess({routes: res.list, cursor: res.cursor, isSubscribeToChannel: payload.isSubscribeToChannel})),
          catchError((error: {
            message: string
          }) => of(RoutesAPiActions.getInfiniteScrollRoutesFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getRoutes = createEffect(
  (actions$ = inject(Actions),
   routesHttpService = inject(RoutesHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(RoutesAPiActions.getRoutes),
      switchMap((payload) => {
        commonsFacadeService.setLoaderOn();
        return routesHttpService.getRoutes(payload.params).pipe(
          map((res: {
            list: RouteModel[]
            cursor: string
          }) => RoutesAPiActions.getRoutesSuccess({routes: res.list, isSubscribeToChannel: payload.isSubscribeToChannel})),
          catchError((error: {
            message: string
          }) => of(RoutesAPiActions.getRoutesFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getRoutesSuccess = createEffect(
  (actions$ = inject(Actions),
   routesChannelService = inject(RoutesChannelService)) => {
    return actions$.pipe(
      ofType(RoutesAPiActions.getRoutesSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          const routeIds = payload.routes.map(r => r.id);
          routeIds.forEach(routeId => routesChannelService.subscribeById(routeId));
        }
      })
    );
  },
  {functional: true, dispatch: false}
);


export const getInfiniteScrollRoutesSuccess = createEffect(
  (actions$ = inject(Actions),
   routesChannelService = inject(RoutesChannelService)) => {
    return actions$.pipe(
      ofType(RoutesAPiActions.getInfiniteScrollRoutesSuccess),
      map((payload) => {
        if (payload.isSubscribeToChannel) {
          const routeIds = payload.routes.map(r => r.id);
          routeIds.forEach(routeId => routesChannelService.subscribeById(routeId));
        }
      })
    );
  },
  {functional: true, dispatch: false}
);

export const subscribeSetRoutesChannels = createEffect(
  (actions$ = inject(Actions),
   routesChannelService = inject(RoutesChannelService)) => {
    return actions$.pipe(
      ofType(RoutesActions.setRoutes),
      map((payload) => {
        const routeIds = payload.routes.map(r => r.id);
        routeIds.forEach(routeId => routesChannelService.subscribeById(routeId));
      })
    );
  },
  {functional: true, dispatch: false}
);

export const subscribeAllRoutesChannel = createEffect(
  (actions$ = inject(Actions),
   routesChanelService = inject(RoutesChannelService)) => {
    return actions$.pipe(
      ofType(RoutesChannelActions.subscribe),
      mergeMap(payload =>
        routesChanelService.getChannelsValues().pipe(
          map((notification) => {
            return RoutesChannelActions.notificationReceived({notification: notification})
          })
        )
      )
    );
  },
  {functional: true}
);

export const subscribeAllPartialRoutesChannel = createEffect(
  (actions$ = inject(Actions),
   routesChannelService = inject(RoutesChannelService)) => {
    return actions$.pipe(
      ofType(RoutesChannelActions.subscribePartial),
      mergeMap(payload =>
        routesChannelService.getPartialChannelsValues().pipe(
          map((notification) => {
            return RoutesChannelActions.partialNotificationReceived({notification: notification})
          })
        )
      )
    );
  },
  {functional: true}
);

export const unsubscribeAllRouteChannels = createEffect(
  (actions$ = inject(Actions),
   routesChannelService = inject(RoutesChannelService)) => {
    return actions$.pipe(
      ofType(RoutesChannelActions.unsubscribe, RoutesActions.removeAll, RoutesActions.clearStore),
      map(payload => {
          routesChannelService.unSubscribeAll();
          return RoutesChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);


export const unsubscribeRouteChannels = createEffect(
  (actions$ = inject(Actions),
   routesChannelService = inject(RoutesChannelService)) => {
    return actions$.pipe(
      ofType(RoutesActions.removeRoutes),
      map(payload => {
          payload.ids?.forEach(routeId => routesChannelService.unSubscribeById(routeId));
          return RoutesChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);





